import classnames from 'classnames';
import { head, values } from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import ErrorModal from 'components/Practice/shared/ErrorModal';
import trackEvent from 'misc/trackEvent';
import { Button, Spinner } from 'ui';
import DropTarget from '../../dropTarget';
import ActionButtons from '../../media_recording/ActionButtons';
import EmbedForm from '../EmbedForm';

class VideoEmbed extends Component {
  state = {
    loading: true,
    uploading: false,
    errorType: null,
    importTimeStarted: null,
    fileValue: '',
  };

  fileInput = React.createRef();

  componentDidMount() {
    window.hippoWidget.onLoad(() => {
      this.setState({ loading: false });
    });
  }

  trackRecordingEvent = (event, data) => {
    if (this.props.isExampleRecording) return;
    trackEvent(event, data);
  };

  trackingEventParameters = () => ({
    lesson_section_element_id: this.props.embedId,
    progress_id: null,
    question_id: null,
    recording_type: null, // no recording type because this is an upload
    upload: true,
  });

  trackUploadStarted = () => {
    window.hippoWidget.on('import_progress', properties => {
      if (properties.operation === 'started') {
        this.setState({
          importTimeStarted: performance.now(),
        });
        this.trackRecordingEvent('recording_in_editor/upload_started', {
          element_id: this.props.embedId,
        });
      }
    });
  };

  trackUploadComplete = () => {
    window.hippoWidget.on('import_complete', data => {
      const importTimingFinal =
        performance.now() - this.state.importTimeStarted;
      this.trackRecordingEvent('recording_in_editor/upload_complete', {
        element_id: this.props.embedId,
        token: data.token,
        duration: data.media_duration,
        total_import_time: importTimingFinal,
      });
      this.trackRecordingEvent('recording_metadata', {
        asset_id: data.asset_id,
        token: data.token,
      });
    });
  };

  trackVideoCancelled = () => {
    const eventData = {
      ...this.trackingEventParameters(),
    };
    this.trackRecordingEvent('hippo_video/recording_cancelled', eventData);
  };

  trackImportFailure = error => {
    const eventData = { element_id: this.props.embedId, error };
    this.trackRecordingEvent('recording_in_editor/upload_failed', eventData);
  };

  trackVideoSubmitted = () => {
    const eventData = {
      ...this.trackingEventParameters(),
    };
    this.trackRecordingEvent('hippo_video/video_submitted', eventData);
  };

  trackSourceUploaded = () => {
    const eventData = {
      ...this.trackingEventParameters(),
    };
    this.trackRecordingEvent('hippo_video/source_uploaded', eventData);
  };

  handleUploadError = error => {
    this.trackImportFailure(error);
    if (error.reason === 'invalid_file_format') {
      this.setState({
        errorType: error.reason + '_video',
        importTimeStarted: null,
      });
    } else if (error.reason === 'size_limit_exceeded') {
      this.setState({
        errorType: error.reason,
      });
    } else if (error.reason === 'processing_error') {
      if (error.error.includes('Media length is too short')) {
        this.setState({
          errorType: 'video_too_small',
        });
      } else if (
        error.error.includes('Video Codec Unidentified') ||
        error.error.includes('Audio Codec Unidentified')
      ) {
        this.setState({
          errorType: 'unidentified_codec',
        });
      }
    }
    this.setState({ uploading: false });
  };

  videoCancelled = () => {
    this.setState({ uploading: false, fileValue: '' });
    this.trackVideoCancelled();
  };

  handleRecord = () => {
    const recordingConfig = {
      screenRecord: false,
      audio: true,
      webCam: true,
      resolution: '1080',
      tabRecording: false,
      separateLayer: false,
      fromPopup: true,
      initiator: 'generic_embed',
    };
    window.hippoWidget.on('record_error', this.handleUploadError);
    window.hippoWidget.startRecording(
      recordingConfig,
      this.props.currentUserId,
    );
    window.hippoWidget.on('source_uploaded', this.trackSourceUploaded);
    window.hippoWidget.on('video_submitted', this.recordToken);
    window.hippoWidget.on('video_cancelled', this.videoCancelled);
  };

  uploadVideo = file => {
    window.hippoWidget.on('import_failed', this.handleUploadError);
    window.hippoWidget.importFile(file, this.props.currentUserId);
    this.trackUploadStarted();
    window.hippoWidget.on('source_uploaded', this.trackSourceUploaded);
    this.trackUploadComplete();
    window.hippoWidget.on('video_submitted', data => {
      this.recordToken(data);
      this.trackVideoSubmitted();
    });
    window.hippoWidget.on('video_cancelled', this.videoCancelled);
  };

  handleFileChange = (e, droppedFiles) => {
    if (this.props.canRecordWithWistia) return;
    const file = head(droppedFiles || e.target.files);

    if (!file) {
      return;
    }

    this.setState({ uploading: true }, () => {
      this.uploadVideo(file);
    });
  };

  recordToken = e => {
    this.props.submitRecording(`${e.recorded_url}?type=lessonly`);
    this.setState({ uploading: false });
  };

  handleResetError = () => {
    this.setState({ errorType: null });
  };

  render() {
    const {
      toggleRecordMenu,
      onSubmit,
      onUrlInput,
      dropInId,
      connectDropTarget,
      isOver,
      canUploadVideo,
      canRecordWithWistia,
      isExampleRecording = false,
      isExampleScreenRecording = false,
      seismicODPScreenUploadEnabled,
      seismicODPWebcamUploadEnabled,
      seismicODPScreenRecordingEnabled,
      seismicODPWebcamRecordingEnabled,
    } = this.props;
    const { loading, showRecordingMenu, uploading, fileValue } = this.state;
    const isRecordingDisabled = values(this.props.browserSupport).every(
      supported => !supported,
    );
    const uploadWithWistia = canRecordWithWistia;
    const showWistiaRecordingButtons = uploadWithWistia;
    const wistiaClassNames = classnames(
      'EditorElement-uploadArea',
      { 'is-over': isOver },
      { 'EditorElement-uploadArea--dropZone': uploadWithWistia },
    );

    if (showWistiaRecordingButtons) {
      return (
        <div
          className={
            uploadWithWistia
              ? 'EditorElement-wistiaUploadArea'
              : 'EditorElement-uploadArea'
          }
        >
          {canUploadVideo &&
            connectDropTarget(
              <div className={wistiaClassNames}>
                <div id={dropInId} className="EditorElement-wistiaContainer">
                  <Spinner />
                </div>
              </div>,
            )}
          {canRecordWithWistia && (
            <Button
              className="EditorElement-wistiaRecordButton mb-12"
              primary
              loading={loading}
              onClick={toggleRecordMenu}
              disabled={isRecordingDisabled || loading}
            >
              {t('lessons.editor.embeds.video.record')}
            </Button>
          )}
          <EmbedForm onUrlInput={onUrlInput} onSubmit={onSubmit} isVideo />
        </div>
      );
    }
    return connectDropTarget(
      <div
        id={dropInId}
        className={classnames(
          { 'is-over': isOver, 'w-fill': isExampleRecording },
          'EditorElement-uploadArea',
          'EditorElement-uploadArea--dropZone',
        )}
      >
        <div className="mb-24">
          {!showRecordingMenu && (
            <ActionButtons
              loading={loading}
              isDisabled={isRecordingDisabled}
              onRecord={
                isExampleScreenRecording || !isExampleRecording
                  ? toggleRecordMenu
                  : this.handleRecord
              }
              saveMedia={this.props.submitRecording}
              onUpload={this.handleFileChange}
              recordText={t('lessons.editor.embeds.video.record')}
              uploadText={t('lessons.editor.embeds.video.upload')}
              fileInput={this.fileInput}
              fileValue={fileValue}
              isUploading={uploading}
              seismicODPScreenUploadEnabled={seismicODPScreenUploadEnabled}
              seismicODPWebcamUploadEnabled={seismicODPWebcamUploadEnabled}
              seismicODPScreenRecordingEnabled={
                seismicODPScreenRecordingEnabled
              }
              seismicODPWebcamRecordingEnabled={
                seismicODPWebcamRecordingEnabled
              }
              isSeismic
            />
          )}
        </div>

        {!isExampleRecording && (
          <EmbedForm onUrlInput={onUrlInput} onSubmit={onSubmit} isVideo />
        )}
        {this.state.errorType && (
          <ErrorModal
            errorType={this.state.errorType}
            onAction={this.handleResetError}
          />
        )}
      </div>,
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  browserSupport: ownProps.browserSupport || state.meta.browserSupport,
  currentUserId: state.editor.currentUserId,
});

export default connect(mapStateToProps)(DropTarget(VideoEmbed));
